import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { arrayMove } from '@dnd-kit/sortable'
import classNames from 'classnames'
import { cloneDeep, isEqual } from 'lodash-es'

import { FLOATING_ELEMENT_PLACEMENT } from '../../../constants/common'
import {
  QA_NAME_CANCEL_BTN,
  QA_NAME_RESET_BTN,
  QA_NAME_SAVE_BTN,
} from '../../../constants/qa'
import { SIZE } from '../../../constants/size'
import {
  useInlineNotifications,
  useNotification,
  useValidationWithoutFormikNotifications,
} from '../../../hooks'
import Button from '../../Button'
import { Checkbox, CHECKBOX_SIZE, CHECKBOX_TYPE } from '../../Checkbox'
import { ConfirmationModal } from '../../Confirmation'
import {
  DRAG_DROP_SORT_STRATEGY,
  DragDropProvider,
  DragDropSortableItem,
  DragDropSortableList,
} from '../../DragDrop'
import { ICON_COLOR, ICON_HEIGHT_16, IconNG } from '../../Icon'
import Modal, { Box, Buttons, Content, Header } from '../../Modal'

import { getDefaultConfig } from './TableConfigModal.api'
import {
  QA_NAME_CONFIG_MODAL,
  QA_NAME_SELECT_ALL,
} from './TableConfigModal.constants'
import {
  getOptionalColumns,
  getSortedColumns,
  hasSelected,
  isAllChecked,
} from './TableConfigModal.utils'

import * as styles from './TableConfigModal.module.css'

import DragDropIcon from '../../../icons/drag-and-drop.svg'

function TableConfigModal({
  columns,
  entity,
  getDefaultColumns,
  params,
  requiredColumns,
  setColumns,
  setConfigOpen,
  onApply,
}) {
  const [isConfirm, setConfirm] = useState(false)
  const [defaultColumns, setDefaultColumns] = useState(null)
  const [isLoading, setLoading] = useState(false)
  const [isSkeleton, setSkeleton] = useState(true)

  const { t } = useTranslation()
  const { handleAddSuccessNotification } = useNotification()
  const { handleAddNotification, handleRemoveNotification, notifications } =
    useInlineNotifications()

  const [initialColumns] = useState(() => cloneDeep(columns))
  const sortedColumns = getSortedColumns(columns)

  const { isValidForm, notificationList } =
    useValidationWithoutFormikNotifications({
      notifications,
      validateData: columns,
      validationSchemaProps: {
        initialValues: initialColumns,
      },
      onRemoveNotification: handleRemoveNotification,
      onSetNotification: handleAddNotification,
    })

  useEffect(() => {
    if (!defaultColumns) {
      const defaultColumnsRequest = getDefaultColumns
        ? getDefaultColumns({ entity, params })
        : getDefaultConfig({ entity, params }).then(data => data.data.columns)

      defaultColumnsRequest.then(nextDefaultColumns => {
        setDefaultColumns(nextDefaultColumns)

        setSkeleton(false)
      })
    }
  }, [defaultColumns, entity, params])

  function getHandleChange(name) {
    return () => {
      setColumns(prevValue => ({
        ...prevValue,
        [name]: {
          ...prevValue[name],
          visible: !prevValue[name].visible,
        },
      }))
    }
  }

  function handleChangeAll() {
    const optionalColumns = getOptionalColumns(sortedColumns, requiredColumns)

    if (isAllChecked(sortedColumns, requiredColumns)) {
      setColumns(
        optionalColumns.reduce(
          (acc, item) => {
            acc[item.name] = { ...item, visible: false }

            return acc
          },
          { ...columns }
        )
      )
    } else {
      setColumns(
        optionalColumns.reduce(
          (acc, item) => {
            acc[item.name] = { ...item, visible: true }

            return acc
          },
          { ...columns }
        )
      )
    }
  }

  function handleDragEnd({ active, over }) {
    if (!over) {
      return
    }

    if (active.id === over.id) {
      return
    }

    const ids = sortedColumns.map(column => column.name)
    const oldIndex = ids.indexOf(active.id)
    const newIndex = ids.indexOf(over.id)

    if (oldIndex < 0 || newIndex < 0) {
      return
    }

    const nextIds = arrayMove(ids, oldIndex, newIndex)

    const newColumns = { ...columns }

    nextIds.forEach((name, index) => {
      newColumns[name] = { ...newColumns[name], index }
    })

    setColumns(newColumns)
  }

  function handleApply() {
    if (!isValidForm()) {
      return
    }

    setLoading(true)

    onApply()
      .then(() => {
        handleAddSuccessNotification('Notification.TableSettingsSavedKey')
      })
      .finally(() => {
        setConfigOpen(false)
      })
  }

  function handleCloseConfig() {
    if (!isEqual(initialColumns, columns)) {
      setConfirm(true)

      return
    }

    setConfigOpen(false)
  }

  function handleReset() {
    setColumns({ ...defaultColumns })
  }

  function handleApplyConfirm() {
    setColumns(initialColumns)
    setConfigOpen(false)
  }

  function handleCancelConfirm() {
    setConfirm(false)
  }

  const draggingStyle = {
    backgroundColor: 'var(--color-bg-surface)',
    borderRadius: '4px',
    boxShadow: '0px 4px 18px var(--color-box-shadow-16)',
  }

  const draggableIds = sortedColumns.map(c => c.name)

  return (
    <>
      {isConfirm ? (
        <ConfirmationModal
          actionButtonsProps={{
            cancelText: t('Modal.CommonGoBackBtnKey'),
            submitText: t('Modal.SaveConfigConfirmBtnKey'),
          }}
          description={t('Modal.SaveConfigConfirmTextKey')}
          onCancel={handleCancelConfirm}
          onSubmit={handleApplyConfirm}
        />
      ) : (
        <Modal qaName={QA_NAME_CONFIG_MODAL} onClose={handleCloseConfig}>
          <Box size={SIZE.MEDIUM}>
            <Header
              isSkeleton={isSkeleton}
              text={t('Modal.TableSettingsTitleKey')}
              onClose={handleCloseConfig}
            />
            <Content size={SIZE.MEDIUM}>
              {notificationList}
              <Checkbox
                checked={isAllChecked(sortedColumns, requiredColumns)}
                className={styles.selectAll}
                disabled={isLoading}
                label={t('Modal.SelectAllLabelKey')}
                qaName={QA_NAME_SELECT_ALL}
                selected={hasSelected(sortedColumns, requiredColumns)}
                size={CHECKBOX_SIZE.small}
                type={CHECKBOX_TYPE.table}
                onChange={handleChangeAll}
              />
              <DragDropProvider onDragEnd={handleDragEnd}>
                <DragDropSortableList
                  items={draggableIds}
                  strategy={DRAG_DROP_SORT_STRATEGY.vertical}
                >
                  {sortedColumns.map(column => {
                    return (
                      <DragDropSortableItem
                        className={classNames(styles.drag, {
                          [styles.disabled]: isLoading,
                        })}
                        draggingStyle={draggingStyle}
                        id={column.name}
                        key={column.name}
                        sortableProps={{ disabled: isLoading }}
                      >
                        <IconNG
                          Icon={DragDropIcon}
                          className={styles.dragIcon}
                          color={ICON_COLOR.TextDisabledColor}
                          size={ICON_HEIGHT_16}
                        />

                        <Checkbox
                          showTooltip
                          checked={column.visible}
                          className={styles.checkbox}
                          disabled={
                            isLoading || requiredColumns.includes(column.name)
                          }
                          iconClassName={styles.checkboxIcon}
                          key={column.index}
                          label={t(column.title)}
                          labelClassName={styles.checkboxLabel}
                          size={CHECKBOX_SIZE.small}
                          type={CHECKBOX_TYPE.table}
                          onChange={getHandleChange(column.name)}
                        />
                      </DragDropSortableItem>
                    )
                  })}
                </DragDropSortableList>
              </DragDropProvider>
            </Content>
            <Buttons size={SIZE.MEDIUM}>
              <Button.Contained
                isLoading={isLoading}
                isSkeleton={isSkeleton}
                qaName={QA_NAME_SAVE_BTN}
                text={t('Modal.SaveConfigBtnKey')}
                onClick={handleApply}
              />
              <Button.Outlined
                isSkeleton={isSkeleton}
                qaName={QA_NAME_CANCEL_BTN}
                text={t('Modal.CancelBtnKey')}
                onClick={handleCloseConfig}
              />
              {defaultColumns && !isEqual(defaultColumns, columns) && (
                <Button.Text
                  disabled={isLoading}
                  qaName={QA_NAME_RESET_BTN}
                  text={t('Modal.DefaultConfigBtnKey')}
                  tooltip={t('Modal.DefaultConfigTooltipKey')}
                  tooltipPlacement={FLOATING_ELEMENT_PLACEMENT.top}
                  onClick={handleReset}
                />
              )}
            </Buttons>
          </Box>
        </Modal>
      )}
    </>
  )
}

export default TableConfigModal
